import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Storage} from "aws-amplify";
import {useTranslation} from "react-i18next";
import {useFeatureFlags} from "utilities/featureUtil";
import {addHTMLContent, addHTMLFormatted} from "redux/actions/messagesActions";
import {formatMessageHTML} from "utilities/formatUtilities";
import {buildStyles} from "connectui/component/buildStyles";
import {useTheme} from "connectui/theme/ThemeProvider";
import FlexContainer from "connectui/component/layout/FlexContainer";
import Text from "connectui/component/display/Text";
import DropdownExpand from "connectui/component/display/dropdown/DropdownExpand";
import CircularSpinner from "connectui/component/display/CircularSpinner";
import SvgIcon from "connectui/component/display/SvgIcon";
import ICONS from "connectui/icons/iconMap";
import ButtonText from "connectui/component/input/button/ButtonText";

const useStyles = (theme) =>
  buildStyles({
    bubble: {
      padding: theme.spacing(2.5, 3.5),
      borderRadius: `${theme.radius.lg}px`,
      width: "100%",
    },
    replyIcon: {
      borderRadius: `${theme.radius.sm}px`,
      width: "20px",
      height: "20px",
      padding: theme.spacing(0.5),
      backgroundColor: theme.colors.gray,
      color: theme.colors.gray25,
    },
  });

const CustomLabel = ({
  subject,
  reply,
  isGuestSender,
  viewOriginal,
  setShowHTML,
}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const {emailPlainTextFix} = useFeatureFlags();
  const {t} = useTranslation();

  return (
    <FlexContainer
      justifyContent={"start"}
      alignItems="center"
      gap={theme.spacing(2)}
      sx={{marginRight: theme.spacing(2), width: "100%"}}
    >
      <Text
        as="div"
        variant="textSM"
        color={isGuestSender ? "gray700" : "white"}
      >
        <Text
          as="span"
          variant="textSM"
          weight="semibold"
          color={isGuestSender ? "gray700" : "white"}
        >
          {"Subject: "}
        </Text>
        {subject}
      </Text>
      {!!reply && (
        <SvgIcon
          component={ICONS["corner-up-left"]}
          className={classes.replyIcon}
          onClick={reply}
        />
      )}
      {emailPlainTextFix && viewOriginal && (
        <ButtonText
          typographyVariant="textSM"
          weight="medium"
          color={isGuestSender ? "white" : "primary"}
          label={t("inbox.conversations.email.show-original")}
          sx={{
            backgroundColor: isGuestSender
              ? theme.colors.primary
              : theme.colors.primary100,
            "&:hover": {
              backgroundColor: isGuestSender
                ? theme.colors.primary600
                : theme.colors.primary200,
            },
            padding: theme.spacing(0.5, 1.5),
          }}
          onClick={(e) => {
            e.stopPropagation();
            setShowHTML(true);
          }}
        />
      )}
    </FlexContainer>
  );
};

const MessageContentEmail = ({
  isGuestSender,
  isHostSender,
  message,
  setIsExpanded = () => {},
  replyEmail = () => {},
  ...props
}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const {emailPlainTextFix} = useFeatureFlags();
  const {subject, html, html_url, body} = message.message;
  const dispatch = useDispatch();
  const htmlContent = useSelector(
    (state) => state.messagesReducer.html_content,
  );
  const htmlCache = useSelector(
    (state) => state.messagesReducer.formatted_html[message.id],
  );

  const [expanded, setExpanded] = useState(false);
  const [showHTML, setShowHTML] = useState(!body);
  const [loadingIFrame, setLoadingIFrame] = useState(true);
  const [loadingHTML, setloadingHTML] = useState(!htmlCache);
  const [formattedHTML, setFormattedHTML] = useState(htmlCache || null);
  const [iframeHeight, setIframeHeight] = useState("0px");
  const viewOriginal =
    !showHTML &&
    (message?.message_translations?.length > 0 ||
      !!message?.message?.html_url ||
      !!message?.message?.html);

  useEffect(() => {
    setIsExpanded(expanded);
  }, [expanded, setIsExpanded]);

  const selectFile = async () => {
    let signed = await Storage.get(html_url, {
      download: true,
      bucket: process.env.REACT_APP_S3_EMAIL_BUCKET,
    });
    signed.Body.text().then((result) => {
      const txt = formatMessageHTML(result, emailPlainTextFix);
      setFormattedHTML((prev) => txt);
      setloadingHTML(false);
      dispatch(addHTMLContent(message.guest_id, txt, html_url));
      dispatch(addHTMLFormatted(message.id, txt));
    });
  };

  const handleIframeLoad = (e) => {
    const el = e.currentTarget;
    const height = el.contentWindow.document.body.scrollHeight;
    setIframeHeight(`${height}px`);
    setLoadingIFrame(false);
  };

  const handleExpand = (expanded) => {
    setExpanded(expanded);

    if (htmlCache) {
      setFormattedHTML(htmlCache);
      return;
    }

    if (!html) {
      const guestHTMLContent = htmlContent[message.guest_id];
      const storedHTML = guestHTMLContent ? guestHTMLContent[html_url] : null;
      if (!storedHTML) selectFile();
      else {
        setFormattedHTML(storedHTML);
        setloadingHTML(false);
        dispatch(addHTMLFormatted(message.id, storedHTML));
      }
    } else {
      // Allow the CircularSpinner to render before html processing
      setTimeout(() => {
        const formatted = formatMessageHTML(html, emailPlainTextFix);
        setFormattedHTML(formatted);
        setloadingHTML(false);
        dispatch(addHTMLFormatted(message.id, formatted));
      }, 50);
    }
  };

  return (
    <DropdownExpand
      className={classes.bubble}
      expanded={expanded}
      onExpand={handleExpand}
      sx={{
        backgroundColor: theme.colors.inputBG,
        padding: 0,
        arrow: {
          color: isHostSender ? theme.colors.gray200 : theme.colors.primary600,
        },
        content: {
          marginTop: theme.spacing(1.5),
          padding: theme.spacing(0, 3.5),
        },
        header: {
          backgroundColor: isHostSender
            ? theme.colors.primary600
            : theme.colors.gray200,
          padding: theme.spacing(2.5, 3.5),
          borderRadius: theme.spacing(
            isGuestSender ? 0 : 2,
            !isGuestSender ? 0 : 2,
            2,
            2,
          ),
        },
      }}
      customLabel={
        <CustomLabel
          viewOriginal={viewOriginal}
          setShowHTML={setShowHTML}
          subject={subject}
          reply={isGuestSender ? () => replyEmail(subject) : undefined}
          isGuestSender={isGuestSender}
        />
      }
    >
      {expanded ? (
        showHTML ? (
          <>
            {(loadingIFrame || loadingHTML) && <CircularSpinner />}
            {!loadingHTML && (
              <iframe
                title="Email content"
                onLoad={handleIframeLoad}
                srcDoc={`${formattedHTML}`}
                width="100%"
                scrolling="no"
                style={{
                  border: "none",
                  width: "100%",
                  height: iframeHeight,
                  display: "block",
                  overflow: "auto",
                }}
              ></iframe>
            )}
          </>
        ) : (
          <Text
            variant="textSM"
            sx={{overflowWrap: "anywhere", whiteSpace: "break-spaces"}}
          >
            {body}
          </Text>
        )
      ) : null}
    </DropdownExpand>
  );
};

export default MessageContentEmail;
